/**
 * The rail, folded into a bottom bar for phones.
 *
 * Ten destinations do not fit across 375px at a tappable size, so five live on
 * the bar and the rest are one tap away behind "More". Targets are 44px tall.
 */
import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { EllipsisIcon } from 'lucide-react'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { NAV_ITEMS, PRIMARY_NAV_ITEMS } from './nav'

export function BottomBar() {
  const [open, setOpen] = useState(false)

  return (
    <nav
      aria-label="Sections"
      className="border-hairline bg-panel fixed inset-x-0 bottom-0 z-30 flex items-stretch border-t pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      {PRIMARY_NAV_ITEMS.map((item) => {
        const Icon = item.icon
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 py-1.5',
                'text-dim focus-visible:outline-ring focus-visible:outline-2 focus-visible:-outline-offset-2',
                isActive && 'text-cyan',
              )
            }
          >
            <Icon className="size-5" aria-hidden />
            <span className="font-mono text-[0.5625rem] tracking-wide uppercase">{item.label}</span>
          </NavLink>
        )
      })}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger
          className={cn(
            'text-dim flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 py-1.5',
            'focus-visible:outline-ring focus-visible:outline-2 focus-visible:-outline-offset-2',
          )}
        >
          <EllipsisIcon className="size-5" aria-hidden />
          <span className="font-mono text-[0.5625rem] tracking-wide uppercase">More</span>
        </SheetTrigger>
        <SheetContent side="bottom" className="pb-[env(safe-area-inset-bottom)]">
          <SheetHeader>
            <SheetTitle className="font-display">All sections</SheetTitle>
          </SheetHeader>
          <ul className="grid grid-cols-2 gap-1 px-4 pb-6">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'hover:bg-panel-2 flex min-h-11 items-center gap-2 rounded-md px-3 text-sm',
                        'focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-2',
                        isActive ? 'text-cyan' : 'text-foreground',
                      )
                    }
                  >
                    <Icon className="size-4" aria-hidden />
                    {item.label}
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </SheetContent>
      </Sheet>
    </nav>
  )
}
